import type {
  AlphaCreateEditBody,
  AlphaDesignVersion,
  AlphaDesignVersionsResponse,
  AlphaEditModelsResponse,
} from "@nlc/shared-types";

import { alphaEditModelsResponseZod } from "@nlc/shared-types";

import { MBC_AI_API_URL } from "@/features/request/mbc-review/config";

function requestUrl(requestId: number, path: string): string {
  return `${MBC_AI_API_URL}/alpha/requests/${requestId}${path}`;
}

async function readError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
    message?: string;
  } | null;

  if (body?.error === "NO_EDIT_ROUNDS") {
    return new Error("You've used all your edit rounds for this creation.");
  }

  return new Error(body?.message ?? body?.error ?? `HTTP ${response.status}`);
}

export async function getAlphaDesignVersions(
  requestId: number,
  designId: string,
): Promise<AlphaDesignVersionsResponse> {
  const response = await fetch(
    requestUrl(requestId, `/designs/${encodeURIComponent(designId)}/versions`),
  );

  if (!response.ok) throw await readError(response);

  return (await response.json()) as AlphaDesignVersionsResponse;
}

export async function createAlphaEdit(
  requestId: number,
  versionId: string,
  body: AlphaCreateEditBody,
): Promise<AlphaDesignVersion> {
  const response = await fetch(
    requestUrl(requestId, `/versions/${encodeURIComponent(versionId)}/edits`),
    {
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) throw await readError(response);

  return (await response.json()) as AlphaDesignVersion;
}

export async function getAlphaVersionImage(
  requestId: number,
  versionId: string,
): Promise<Blob> {
  const response = await fetch(
    requestUrl(requestId, `/versions/${encodeURIComponent(versionId)}/image`),
  );

  if (!response.ok) throw await readError(response);

  return response.blob();
}

export async function getAlphaEditModels(): Promise<AlphaEditModelsResponse> {
  const response = await fetch(`${MBC_AI_API_URL}/alpha/edit-models`);

  if (!response.ok) throw await readError(response);

  return alphaEditModelsResponseZod.parse(await response.json());
}
